
'use client';

import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import type { GameReview, UpsertGameReview } from '@/types';
import { db, storage } from '@/lib/firebase';
import { collection, onSnapshot, doc, updateDoc, query, orderBy, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from './AuthContext';
import { v4 as uuidv4 } from 'uuid';

interface GameReviewContextType {
    reviews: GameReview[];
    loading: boolean;
    createReview: (name: string) => Promise<void>;
    updateReview: (reviewId: string, reviewData: UpsertGameReview) => Promise<void>;
}

const GameReviewContext = createContext<GameReviewContextType | undefined>(undefined);

export const GameReviewProvider = ({ children }: { children: ReactNode }) => {
    const [reviews, setReviews] = useState<GameReview[]>([]);
    const [loading, setLoading] = useState(true);
    const { toast } = useToast();
    const { user, isOwner } = useAuth();

    useEffect(() => {
        const q = query(collection(db, "gameReviews"), orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const reviewsData: GameReview[] = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                reviewsData.push({
                    id: doc.id,
                    name: data.name,
                    gameplay: data.gameplay,
                    gameDesign: data.gameDesign,
                    rating: data.rating,
                    tags: data.tags,
                    coverUrl: data.coverUrl,
                    coverPath: data.coverPath,
                    iconUrl: data.iconUrl,
                    iconPath: data.iconPath,
                    createdAt: data.createdAt
                });
            });
            setReviews(reviewsData);
            setLoading(false);
        }, (error) => {
            console.error("Firestore snapshot error (reviews):", error);
            toast({
                title: "Connection Error",
                description: "Could not load game reviews.",
                variant: "destructive",
            });
            setLoading(false);
        });

        return () => unsubscribe();
    }, [toast]);
    
    const createReview = async (name: string) => {
        if (!user || !isOwner) return;

        try {
            const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            const reviewRef = doc(db, 'gameReviews', slug);

            const newReview: Omit<GameReview, 'id'> = {
                name: name,
                gameplay: "Gameplay analysis to be written.",
                gameDesign: "Game design analysis to be written.",
                rating: 0,
                tags: [],
                coverUrl: `https://placehold.co/1600x400.png`,
                iconUrl: `https://placehold.co/40x40.png`,
                createdAt: serverTimestamp() as any,
            };
            await setDoc(reviewRef, newReview);
            toast({ title: "Review Added", description: `The review for ${name} has been created.` });

        } catch (error) {
            console.error("Error creating review:", error);
            toast({ title: "Error", description: "Failed to create the review.", variant: "destructive" });
        }
    };

    const updateReview = async (reviewId: string, reviewData: UpsertGameReview) => {
        if (!user || !isOwner) return;

        try {
            const reviewRef = doc(db, 'gameReviews', reviewId);
            const updatePayload: any = {
                gameplay: reviewData.gameplay,
                gameDesign: reviewData.gameDesign,
                rating: reviewData.rating,
                tags: reviewData.tags.split(',').map(tag => tag.trim()).filter(Boolean),
            };

            if (reviewData.coverFile) {
                 const coverPath = `covers/games/${reviewId}/${uuidv4()}`;
                 const storageRef = ref(storage, coverPath);
                 await uploadBytes(storageRef, reviewData.coverFile);
                 const coverUrl = await getDownloadURL(storageRef);
                 updatePayload.coverUrl = coverUrl;
                 updatePayload.coverPath = coverPath;
            }

            if (reviewData.iconFile) {
                const iconPath = `icons/games/${reviewId}/${uuidv4()}`;
                const storageRef = ref(storage, iconPath);
                await uploadBytes(storageRef, reviewData.iconFile);
                const iconUrl = await getDownloadURL(storageRef);
                updatePayload.iconUrl = iconUrl;
                updatePayload.iconPath = iconPath;
            }

            await updateDoc(reviewRef, updatePayload);

        } catch (error) {
             console.error("Error updating review:", error);
             toast({ title: "Error", description: "Failed to update the review.", variant: "destructive" });
             throw error;
        }
    };

    return (
        <GameReviewContext.Provider value={{ reviews, loading, createReview, updateReview }}>
            {children}
        </GameReviewContext.Provider>
    );
};

export const useGameReviews = () => {
    const context = useContext(GameReviewContext);
    if (context === undefined) {
        throw new Error('useGameReviews must be used within a GameReviewProvider');
    }
    return context;
};
